import {openConfirmDialog, openDialog, Segment, showNotification} from 'platform/components';
import {HStack, Text, VStack} from 'platform/foundation';

import {useRef, useState} from 'react';

import {
  useCopyConfigurationMutation,
  useDeleteConfigurationMutation,
  useLazyGetConfigurationDetailQuery,
} from '@dms/api/new-vehicle';
import {
  ConfigurationCustomerView,
  getOrderConfigurationDialogId,
  getRegisterExternalOrderDialogId,
  OrderConfigurationDialog,
  RegisterExternalOrderDialog,
} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  usePermissions,
} from '@dms/shared';

import {composePath, useNavigate, useOnMount} from 'shared/web';

import {type ActionCallback} from 'features/eag-grid';

import {changeConfigurationPreset} from './changeConfigurationPreset';
import {CONFIGURATION_GRID_CODE} from './CONFIGURATION_GRID_CODE';
import {
  type ConfigurationPreset,
  getConfigurationPresetFilter,
} from './getConfigurationPresetFilter';
import {handleConfigurationGridAction} from './handleConfigurationGridAction';

const PRESET_OPTIONS: {value: ConfigurationPreset; label: string}[] = [
  {
    value: 'all',
    label: i18n.t('general.labels.all', {defaultValue: 'All'}),
  },
  {
    value: 'onOrder',
    label: i18n.t('entity.newVehicle.presets.onOrder', {defaultValue: 'On order'}),
  },
  {
    value: 'arrived',
    label: i18n.t('entity.newVehicle.presets.arrived', {defaultValue: 'Arrived'}),
  },
  {
    value: 'waitingForConfirmation',
    label: i18n.t('entity.newVehicle.presets.waitingForConfirmation', {
      defaultValue: 'Waiting for confirmation',
    }),
  },
  {
    value: 'unassigned',
    label: i18n.t('entity.newVehicle.presets.unassigned', {defaultValue: 'Unassigned'}),
  },
];

const PREDEFINED_COLUMNS = ['vehicleId', 'businessCaseId', 'interestId'] as const;

export function NewVehicleConfigurations() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState<ConfigurationPreset>('all');
  const [copyConfiguration] = useCopyConfigurationMutation();
  const [deleteConfiguration] = useDeleteConfigurationMutation();
  const [getConfigurationDetail] = useLazyGetConfigurationDetailQuery();
  const isMounted = useRef(true);
  const detailRequestSequence = useRef(0);
  useOnMount(
    () => {
      isMounted.current = true;
    },
    () => {
      isMounted.current = false;
    }
  );
  const [canWrite, canOrder, canRegisterExternal] = usePermissions({
    permissionKeys: [
      'newVehicleConfigurationWrite',
      'newVehicleConfigurationOrder',
      'newVehicleConfigurationRegisterExternal',
    ],
  });

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
  }) => {
    if (Array.isArray(rowId)) {
      return;
    }

    handleConfigurationGridAction(actionKey, rowId, {
      canWrite: Boolean(canWrite),
      canOrder: Boolean(canOrder),
      canRegisterExternal: Boolean(canRegisterExternal),
      vehicleId: getColumnData<string>('vehicleId')?.value,
      businessCaseId: getColumnData<string>('businessCaseId')?.value,
      interestId: getColumnData<string>('interestId')?.value,
      navigate,
      copyConfiguration: async (configurationId) => {
        await copyConfiguration({
          configurationId,
          copyConfigurationRequestBody: {},
        }).unwrap();
      },
      deleteConfiguration: async (configurationId) => {
        await deleteConfiguration({configurationId}).unwrap();
      },
      confirmDelete: (onConfirm) =>
        openConfirmDialog({
          text: i18n.t('entity.newVehicle.confirmations.deleteConfiguration', {
            defaultValue: 'Delete this configuration?',
          }),
          onConfirm,
        }),
      refreshData,
      viewAsCustomer: (configurationId) =>
        openDialog(<ConfigurationCustomerView configurationId={configurationId} />, {
          size: 'large',
          title: i18n.t('entity.newVehicle.actions.viewAsCustomer', {
            defaultValue: 'View as customer',
          }),
        }),
      openOrder: (configurationId) => {
        const sequence = detailRequestSequence.current + 1;
        detailRequestSequence.current = sequence;
        void getConfigurationDetail({configurationId}, true)
          .unwrap()
          .then((configuration) => {
            if (!isMounted.current || detailRequestSequence.current !== sequence) {
              return;
            }
            const dialogId = getOrderConfigurationDialogId(configurationId);
            openDialog(
              <OrderConfigurationDialog
                configuration={configuration}
                dialogId={dialogId}
                onDone={refreshData}
              />,
              {
                id: dialogId,
                size: 'large',
                title: i18n.t('entity.newVehicle.order.title', {
                  defaultValue: 'Order configuration',
                }),
              }
            );
          })
          .catch(handleApiError);
      },
      openRegisterExternal: (configurationId) => {
        const sequence = detailRequestSequence.current + 1;
        detailRequestSequence.current = sequence;
        void getConfigurationDetail({configurationId}, true)
          .unwrap()
          .then((configuration) => {
            if (!isMounted.current || detailRequestSequence.current !== sequence) {
              return;
            }
            const dialogId = getRegisterExternalOrderDialogId(configurationId);
            openDialog(
              <RegisterExternalOrderDialog
                configuration={configuration}
                dialogId={dialogId}
                onDone={refreshData}
              />,
              {
                id: dialogId,
                size: 'large',
                title: i18n.t('entity.newVehicle.external.title', {
                  defaultValue: 'Register as externally ordered / already stocked',
                }),
              }
            );
          })
          .catch(handleApiError);
      },
      notifyLater: () =>
        showNotification.info(
          i18n.t('entity.newVehicle.notifications.availableLater', {
            defaultValue: 'This action arrives in a later release',
          })
        ),
      notifyPermissionRequired: () =>
        showNotification.warning(i18n.t('general.labels.noPermission')),
      handleError: handleApiError,
      handleUnhandled: catchUnhandledDataGridActions,
    });
  };

  const configurePath = composePath(vehiclesRoutes.newVehicleConfigurator, {
    params: {step: '1'},
  });

  return (
    <VStack spacing={3}>
      <HStack spacing={4} align="center" wrap>
        <Segment
          options={PRESET_OPTIONS}
          value={preset}
          onChange={(nextPreset) => changeConfigurationPreset(nextPreset, setPreset)}
          label={i18n.t('entity.newVehicle.presets.label', {
            defaultValue: 'Configuration presets',
          })}
        />
        <Text size="small" color="secondary">
          {i18n.t('entity.newVehicle.presets.caption', {
            defaultValue: 'Focus the list by order stage or assignment',
          })}
        </Text>
      </HStack>
      <DmsDataGrid
        key={preset}
        gridCode={CONFIGURATION_GRID_CODE}
        predefinedColumns={PREDEFINED_COLUMNS}
        actionCallback={actionCallback}
        queryModifier={(filter) => getConfigurationPresetFilter(preset, filter)}
        emptyState={{
          headline: i18n.t('entity.newVehicle.configurations.empty', {
            defaultValue: 'No configurations yet',
          }),
          subheadline: i18n.t('entity.newVehicle.configurations.emptySubheadline', {
            defaultValue: 'Configure a new vehicle to create the first one',
          }),
          action: canWrite
            ? {
                title: i18n.t('entity.newVehicle.actions.configure', {
                  defaultValue: 'Configure new vehicle',
                }),
                onClick: () => navigate(configurePath),
              }
            : undefined,
        }}
      />
    </VStack>
  );
}
